// V1.2 : Composant d'autocomplétion d'adresse via l'API gouvernementale
// https://adresse.data.gouv.fr/api-doc/adresse — gratuite, sans clé API
import { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from './input';

/**
 * AddressAutocomplete — champ d'adresse avec suggestions temps réel.
 *
 * Props :
 * - value        : objet { adresse, code_postal, ville } ou string
 * - onChange     : (next) => void  ; next = { adresse, code_postal, ville, citycode, context }
 * - placeholder  : string (défaut: "Commencez à taper votre adresse...")
 * - accentClass  : classes Tailwind pour focus (ex: "focus:border-cyan-400")
 * - disabled     : bool
 */
export function AddressAutocomplete({
  value,
  onChange,
  placeholder = 'Commencez à taper votre adresse...',
  accentClass = 'focus:border-cyan-400',
  disabled = false,
}) {
  const initial = typeof value === 'string'
    ? value
    : (value?.adresse || '');
  const [query, setQuery] = useState(initial);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Fermer la liste quand on clique ailleurs
  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounce des requêtes vers l'API gouv
  useEffect(() => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=6&autocomplete=1`
        );
        const data = await res.json();
        // DDD.3 : reconstruction robuste selon le type d'item retourné par l'API gouv
        // - housenumber : "12 Rue de la Paix" (numéro + rue)
        // - street      : "Rue de la Paix" (rue seule)
        // - locality / municipality : pas d'adresse précise, on garde le label complet
        const feats = (data?.features || []).map((f) => {
          const p = f.properties || {};
          let adresse = '';
          if (p.type === 'housenumber') {
            adresse = `${p.housenumber || ''} ${p.street || p.name || ''}`.trim();
          } else if (p.type === 'street') {
            adresse = (p.street || p.name || '').trim();
          } else {
            // locality / municipality / poi : pas de rue, on laisse le user compléter
            adresse = '';
          }
          // Fallback final : si tout est vide, utiliser p.name puis label
          if (!adresse) adresse = p.name || p.label || '';
          return {
            label: p.label || adresse,
            adresse,
            code_postal: p.postcode || '',
            ville: p.city || p.municipality || '',
            citycode: p.citycode || '',
            context: p.context || '',
            type: p.type || '',
          };
        });
        setSuggestions(feats);
        setOpen(true);
      } catch (err) {
        // Silencieux — en cas d'erreur réseau, on ne bloque pas la saisie manuelle
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (sug) => {
    setQuery(sug.label);
    setSuggestions([]);
    setOpen(false);
    // DDD.8 : on garantit que `adresse` n'est jamais vide même quand l'API retourne
    // un type municipality / locality. Dans ce cas on utilise le label complet.
    const safeAdresse = sug.adresse && sug.adresse.trim().length > 0
      ? sug.adresse
      : (sug.label || '').replace(`, ${sug.code_postal || ''} ${sug.ville || ''}`.trim(), '').trim() || sug.label || '';
    onChange && onChange({ ...sug, adresse: safeAdresse });
  };

  const handleManualChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    // on ne touche pas code_postal/ville tant que le client ne sélectionne pas une suggestion
    // mais on remonte l'adresse tapée manuellement
    onChange && onChange({ adresse: v, code_postal: value?.code_postal || '', ville: value?.ville || '', manual: true });
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
        <Input
          value={query}
          onChange={handleManualChange}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`pl-10 pr-10 bg-gray-800/50 border-gray-700 ${accentClass}`}
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400 animate-spin" />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-gray-900 border border-cyan-400/30 rounded-lg shadow-2xl max-h-72 overflow-y-auto">
          {suggestions.map((sug, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelect(sug)}
              className="w-full text-left px-4 py-2.5 hover:bg-cyan-400/10 border-b border-gray-800 last:border-0 transition-colors"
            >
              <p className="text-white text-sm">{sug.label}</p>
              <p className="text-gray-500 text-xs font-mono mt-0.5">{sug.context}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
